// RefundsTab.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import adminServices from '../../services/AdminServices';
import Loading from '../feedback/Loading';
import FetchError from '../feedback/FetchError';
import { FaSearch } from 'react-icons/fa';
import RefundsTable from './RefundsTable';
import RefundModal from './RefundModal';
import { useAuth } from '../../context/UserContext';
import paymentsService from '../../services/PaymentServices';
import vendorServices from '../../services/VendorSevices';
import RefundStats from '../dashboard/RefundStats';

function RefundsTab() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refunds, setRefunds] = useState(null);
    const [activeRefund, setActiveRefund] = useState(null);
    const searchInputRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();

    const currentOrdering = searchParams.get('ordering') || '';

    const getRefunds = useCallback((queryParams = '') => {
        setError(null);
        setLoading(true);

        let service;
        if (user?.is_admin) {
            service = adminServices;
        } else if (user?.is_vendor) {
            service = vendorServices;
        } else {
            service = paymentsService;
        }

        service.getRefunds(queryParams ? `?${queryParams}` : '')
            .then((res) => {
                setRefunds(res.data);
                setLoading(false);
            })
            .catch((err) => {
                console.error('Failed to fetch refunds:', err);
                setError(err);
                setLoading(false);
            });
    }, [user]);

    useEffect(() => {
        getRefunds();
    }, [getRefunds]);

    useEffect(() => {
        const params = searchParams.toString();
        getRefunds(params);
    }, [searchParams, getRefunds]);

    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            const query = e.target.value.trim();
            setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                if (query) {
                    newParams.set('search', query);
                } else {
                    newParams.delete('search');
                }
                newParams.delete('page');
                return newParams;
            });
        }
    };

    const changePageParam = (page) => {
        setSearchParams((params) => {
            const newParams = new URLSearchParams(params);
            if (page) {
                newParams.set('page', page);
            } else {
                newParams.delete('page');
            }
            return newParams;
        });
    };

    const updateURL = (newParams, scroll = false) => {
        const newPath = {
            pathname: location.pathname,
            search: newParams.toString(),
        };
        navigate(newPath, { replace: true, state: { scroll } });
    };

    const handleOrderingChange = (e) => {
        const value = e.target.value;
        const params = new URLSearchParams(searchParams);

        if (value) {
            params.set('ordering', value);
        } else {
            params.delete('ordering');
        }
        params.delete('page');

        updateURL(params);
    };

    const openModal = (refund) => {
        const openBtn = document.getElementById('refund-open-btn');
        setActiveRefund(refund);
        openBtn.click()
    };

    return (
        <div className="space-y-6">
            <RefundStats />

            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-800">Refunds</h1>
                {refunds?.count !== undefined && (
                    <p className="text-lg font-semibold text-gray-600">
                        Total Refunds: <span className="text-primary">({refunds.count})</span>
                    </p>
                )}
            </div>

            <div className="space-y-6">
                {loading ? (
                    <div className="py-9">
                        <Loading />
                    </div>
                ) : error ? (
                    <FetchError error={error} />
                ) : (
                    <div>
                        <div className="flex flex-col md:flex-row items-center justify-between mb-8">
                            <div className="relative w-full md:w-80">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                    <FaSearch />
                                </span>
                                <input
                                    ref={searchInputRef}
                                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-primary focus:border-primary"
                                    placeholder="Search by customer, product, status..."
                                    type="text"
                                    onKeyDown={handleSearch}
                                    defaultValue={searchParams.get('search') || ''}
                                />
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4 sm:mb-0">
                                <p className="whitespace-nowrap">Sort By:</p>
                                <select
                                    className="select w-full sm:w-auto border"
                                    value={currentOrdering}
                                    onChange={handleOrderingChange}
                                >
                                    <option value="">Default</option>
                                    <option value="-creation_time">Newest</option>
                                    <option value="creation_time">Oldest</option>
                                    <option value="-last_updated_time">Recently Updated</option>
                                    <option value="last_updated_time">Least Recently Updated</option>
                                    {(user?.is_admin || user?.is_vendor) && (
                                        <>
                                            <option value="owner__email">Owner Email: A to Z</option>
                                            <option value="-owner__email">Owner Email: Z to A</option>
                                            <option value="owner__name">Owner Name: A to Z</option>
                                            <option value="-owner__name">Owner Name: Z to A</option>
                                        </>
                                    )}
                                    <option value="cart_item__product__price">Price: Low to High</option>
                                    <option value="-cart_item__product__price">Price: High to Low</option>
                                </select>
                            </div>
                        </div>

                        <RefundsTable
                            refunds={refunds}
                            changePageParam={changePageParam}
                            openModal={openModal}
                        />

                        <RefundModal
                            action={1}
                            activeRefund={activeRefund}
                            onSubmitSuccess={getRefunds}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default RefundsTab;